/**
 * Helpers for constructing URL-element overlay links.
 *
 * The same overlay URL string is rendered in two places (URL Builder Drawer
 * "generated URL" / payload, and Overlay Snippets card). They previously had
 * subtly different fallback rules for empty paths and empty domains, which
 * meant the saved `generatedURL` could differ from what merchants copy. This
 * module is the single source of truth so both call sites always agree.
 */

/**
 * Normalize a domain so it has a protocol and no trailing slash.
 * Returns "" for empty/whitespace input.
 */
export function normalizeDomain(domain: string | undefined | null): string {
  if (!domain) return '';
  let normalized = domain.trim().replace(/\/+$/, '');
  if (!normalized) return '';
  if (!/^https?:\/\//i.test(normalized)) {
    normalized = `https://${normalized}`;
  }
  return normalized;
}

/**
 * Encode a page path's segments. Returns "" for empty input, otherwise
 * "/seg1/seg2" with each segment passed through encodeURIComponent.
 */
export function encodePagePath(pagePath: string | undefined | null): string {
  if (!pagePath) return '';
  const trimmed = pagePath.replace(/^\/+/, '');
  if (!trimmed) return '';
  return `/${trimmed.split('/').map(encodeURIComponent).join('/')}`;
}

export type OverlayTriggerType = 'donate' | 'attend';

interface BuildOverlayElementURLArgs {
  domain: string | undefined | null;
  pagePath?: string;
  type: OverlayTriggerType;
  formID: string;
  /** Omit (or pass falsy) for the form-builder snippet (no elementid in URL). */
  elementID?: string;
}

/**
 * Build the overlay-trigger URL that the merchant pastes into their site.
 * Shape:
 *   {domain}{path}?{type}&formid={formID}[&elementid={elementID}]
 *
 * Empty path → no leading "/" before "?":
 *   "https://example.org?donate&formid=ABC&elementid=XYZ"
 *
 * Empty domain → relative URL (mirrors OverlaySnippets' historical behavior):
 *   "?donate&formid=ABC&elementid=XYZ"
 */
export function buildOverlayElementURL(args: BuildOverlayElementURLArgs): string {
  const normalizedDomain = normalizeDomain(args.domain);
  const path = encodePagePath(args.pagePath);
  // Encode formID/elementID defensively so a stray "&", "=", "+", or "#" in
  // an ID can never silently corrupt parameters at parse time. Today's IDs
  // are URL-safe UUIDs but nothing in the type system enforces that.
  const encodedFormID = encodeURIComponent(args.formID);
  const params = args.elementID
    ? `${args.type}&formid=${encodedFormID}&elementid=${encodeURIComponent(args.elementID)}`
    : `${args.type}&formid=${encodedFormID}`;
  if (!normalizedDomain) {
    return `?${params}`;
  }
  return `${normalizedDomain}${path}?${params}`;
}
